import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import {
  Card, BlockStack, InlineStack, Button, IndexTable, Link,
} from '@shopify/polaris';
import { PlusIcon, DeleteIcon } from '@shopify/polaris-icons';

import { loginCodesIndex, loginCodeCreate, loginCodeDelete } from '../../redux/actions/loginCodes';
import { locationsIndex } from '../../redux/actions/locations';
import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';

import NewLoginCodeDialog from './NewLoginCodeDialog';
import DeleteLoginCodeDialog from './DeleteLoginCodeDialog';

const LoginCodes = ({ loginCodes, locations, getLoginCodes, loadLocations, createLoginCode, deleteLoginCode }) => {
  const [activeLoginCode, setActiveLoginCode] = useState(null);
  const [displayNewLoginCodeDialog, setDisplayNewLoginCodeDialog] = useState(false);
  const [displayDeleteLoginCodeDialog, setDisplayDeleteLoginCodeDialog] = useState(false);
  const [, setSubmitted] = useState(false);

  useEffect(() => {
    getLoginCodes();
    loadLocations();
  }, [getLoginCodes, loadLocations]);

  const locationLabel = (locationId) => {
    const location = locations.locations.find(location => String(location.id) === String(locationId));

    return location ? location.label : locationId;
  }

  const showDeleteDialog = (loginCode) => {
    setActiveLoginCode(loginCode);
    setDisplayDeleteLoginCodeDialog(true);
  }

  const loginCodesTableData = () => {
    let loginCodesItemData = [];

    loginCodes.loginCodes.forEach((loginCode) => {
      loginCodesItemData.push({
        loginCode: loginCode.login_code,
        warehouseCode: loginCode.warehouse_code,
        qrCode: loginCode.qr_code,
        id: loginCode.id,
        locationId: loginCode.degenerate_location_id,
        locationLabel: locationLabel(loginCode.degenerate_location_id),
        expiresAt: loginCode.expires_at
      });
    })

    return loginCodesItemData;
  }

  const codeRows = loginCodesTableData();

  return(
    <SettingsPage title="Warehouse Login Codes" description="">
      <Card>
        <BlockStack gap="400">
          { !loginCodes.isFetching && loginCodes.loginCodes &&
            <>
              <IndexTable
                resourceName={{ singular: 'login code', plural: 'login codes' }}
                itemCount={codeRows.length}
                selectable={false}
                headings={[
                  { title: 'Login Code' },
                  { title: 'QR Code' },
                  { title: 'Location' },
                  { title: 'Expires' },
                  { title: 'Actions' },
                ]}
              >
                {codeRows.map((loginCode, index) => (
                  <IndexTable.Row id={loginCode.id || `${loginCode.locationId}-${loginCode.loginCode}`} key={index} position={index}>
                    <IndexTable.Cell>{loginCode.warehouseCode}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {loginCode.qrCode && (
                        <>
                          <img src={loginCode.qrCode} width={80} height={80} alt="Login code QR code" />
                          <br/>
                          <Link url={loginCode.qrCode} download={`login-code-${loginCode.id}.png`}>Download QR</Link>
                        </>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>{loginCode.locationLabel}</IndexTable.Cell>
                    <IndexTable.Cell>{loginCode.expiresAt ? new Date(loginCode.expiresAt).toLocaleString() : ''}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <Button
                        tone="critical"
                        icon={DeleteIcon}
                        onClick={() => showDeleteDialog(loginCode)}
                        loading={loginCodes.deletingLoginCode === loginCode.loginCode}
                        disabled={loginCodes.deletingLoginCode === loginCode.loginCode}
                      >
                        Remove Code
                      </Button>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>

              { loginCodes.isCreating &&
                <LoadingSpinner size="small" statusText="Adding login code"/>
              }

              <InlineStack align="start">
                <Button
                  variant="primary"
                  icon={PlusIcon}
                  onClick={() => setDisplayNewLoginCodeDialog(true)}
                >
                  New Login Code
                </Button>
              </InlineStack>
            </>
          }

          { loginCodes.isFetching &&
            <LoadingSpinner size="small" statusText="Loading login codes"/>
          }
        </BlockStack>
      </Card>

      { displayNewLoginCodeDialog &&
        <NewLoginCodeDialog
          locations={locations}
          setDisplayNewLoginCodeDialog={setDisplayNewLoginCodeDialog}
          createLoginCode={createLoginCode}
          setSubmitted={setSubmitted}
          displayNewLoginCodeDialog={displayNewLoginCodeDialog}
        />
      }

      { displayDeleteLoginCodeDialog &&
        <DeleteLoginCodeDialog
          loginCode={activeLoginCode}
          setDisplayDeleteLoginCodeDialog={setDisplayDeleteLoginCodeDialog}
          deleteLoginCode={deleteLoginCode}
          setSubmitted={setSubmitted}
          displayDeleteLoginCodeDialog={displayDeleteLoginCodeDialog}
        />
      }

    </SettingsPage>
  )
}

const mapStateToProps = state => ({
  loginCodes: state.loginCodes,
  locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
  getLoginCodes: () => dispatch(loginCodesIndex()),
  loadLocations: () => dispatch(locationsIndex()),
  createLoginCode: (loginCode) => dispatch(loginCodeCreate(loginCode)),
  deleteLoginCode: (loginCode) => dispatch(loginCodeDelete(loginCode))
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginCodes);
